import { useRef, useEffect, useMemo, useContext } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { useTheme } from '@techstyle/react-components';
import { useDomain } from '@techstyle/redux-core';

import { afterpayMessaging } from '../../utils/afterpayMessaging';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import {
  getStorePaymentSettings,
  getStorePaymentTypes,
} from '../../utils/selectors';
import { Component as PaymentMethodProvider } from '../PaymentMethodProvider';

export default function AfterpayMessaging({
  afterpayClassName,
  afterpayAmount,
  excludedProducts,
}) {
  const apMessaging = useRef(null);
  const { tld } = useDomain();
  const { colors } = useTheme();
  const iconColor = colors.grey;
  const infoIcon = useMemo(
    () =>
      `<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 512 512" size="14" color=${iconColor} style="color:${iconColor}" height="14" width="14" xmlns="http://www.w3.org/2000/svg"><path d="M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 110c23.196 0 42 18.804 42 42s-18.804 42-42 42-42-18.804-42-42 18.804-42 42-42zm56 254c0 6.627-5.373 12-12 12h-88c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h12v-64h-12c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h64c6.627 0 12 5.373 12 12v100h12c6.627 0 12 5.373 12 12v24z"></path></svg>`,
    [iconColor]
  );
  const {
    'additional-payment-methods': additionalPaymentMethods,
    cashapp: isCashAppEnabled,
  } = useLDFlags();
  const isAfterPayEnabled =
    additionalPaymentMethods === 'afterpay' ||
    additionalPaymentMethods === 'both';
  const isKlarnaEnabled =
    additionalPaymentMethods === 'klarna' ||
    additionalPaymentMethods === 'both';

  const paymentTypes = useSelector(state =>
    getStorePaymentTypes(state, {
      isAfterPayEnabled,
      isCashAppEnabled,
      isKlarnaEnabled,
    })
  );
  const paymentSettings = useSelector(state => getStorePaymentSettings(state));
  const isAfterpayReady = useContext(
    PaymentMethodProvider.Context
  )?.isAfterPayReady;

  useEffect(() => {
    if (
      isAfterpayReady &&
      paymentTypes.includes('afterpay') &&
      paymentSettings.afterpay
    ) {
      const min = parseInt(paymentSettings.afterpay.minimumAmount * 100); // cents
      const max = parseInt(paymentSettings.afterpay.maximumAmount * 100); // cents

      const amount = parseInt(afterpayAmount * 100); // cents

      const customizedConfig = {
        // afterpay doesn't work with even price like $10, $20 targeted by price selector
        // so we are using amount here to make it work for all prices
        // to do this, we need to convert the price to cents
        amount: amount,
        priceSelector: `.${afterpayClassName}`,
        excludedProducts: excludedProducts,
        minMaxThreshold: {
          min: min, // cents,
          max: max, // cents
        },
        showUpperLimit: amount > max,
        replaceModalOpenIcon: {
          innerHTML: infoIcon,
        },
      };

      const apMessagingInstance = afterpayMessaging(tld, {
        ...customizedConfig,
      });
      if (!apMessaging.current) {
        apMessaging.current = apMessagingInstance;
        apMessaging.current.init();
      } else {
        apMessaging.current = apMessagingInstance;
        apMessaging.current.refresh();
      }
    }
  }, [
    tld,
    afterpayClassName,
    afterpayAmount,
    excludedProducts,
    infoIcon,
    isAfterpayReady,
    paymentTypes,
    paymentSettings,
  ]);

  // afterpay messaging is automatically generated based on the config
  // and it will be located under the element with the given afterpayClassName
  return null;
}

AfterpayMessaging.propTypes = {
  afterpayClassName: PropTypes.string.isRequired,
  afterpayAmount: PropTypes.number.isRequired,
};
AfterpayMessaging.defaultProps = {
  excludedProducts: null,
};
